import TopBar from './views/top-bar';
import { MobileSwipeableDrawer, CircularProgress, Box } from 'src/components';
import { useState } from 'react';
import Filters from './views/filters';
import { useIsMobile } from 'src/hooks';
import MapDialogMobile from './views/map-dialog-mobile';
import UserVisibilityButton from './components/user-visibility-button';
import FeaturesTravellersList from './views/features-travellers-list';
import Search from './views/search';
import { useAuth } from 'src/utils';
import { useFeatureTravellerStore, useMapFiltersStore, useMapStore } from 'src/stores';
import { ALL_TYPES, ALL_COUNTRIES } from 'src/data';

export default function MapPageLayout({ 
    children,  
    usersLoading,
    filteredPlaces,
    toggleSelectedTypes,
    showDrawer,
    showDrawerView,
    setShowDrawerView,
    isDesktopViewExpanded,
    handleShowLocations,
    onToggleFavourites,
    footerHeight,
    locationPermissionDenied,
    updateLocationAndFetchUsers,
    setMobileDrawerState}) {

    const isMobile = useIsMobile();
    const { user } = useAuth();
    const allTypes = ALL_TYPES;
    const allCountries = ALL_COUNTRIES;

    const [currentView, setCurrentView] = useState("AttractionsTravellersList");
    const [searchValue, setSearchValue] = useState('');

    const { flyTo } = useMapStore();

    const travellers = useFeatureTravellerStore((state) => state.travellers);
    const setSelectedTraveller = useFeatureTravellerStore((state) => state.setSelectedTraveller);
    const setSelectedFeature = useFeatureTravellerStore((state) => state.setSelectedFeature);

    const selectedTypes = useMapFiltersStore((state) => state.selectedTypes);
    const setSelectedTypes = useMapFiltersStore((state) => state.setSelectedTypes);
    const selectedCountries = useMapFiltersStore((state) => state.selectedCountries);
    const setSelectedCountries = useMapFiltersStore((state) => state.setSelectedCountries);
    const showFavouritesOnly = useMapFiltersStore((state) => state.showFavouritesOnly);
    const showTravellers = useMapFiltersStore((state) => state.showTravellers);
    const setShowTravellers = useMapFiltersStore((state) => state.setShowTravellers);
    const showFriendsOnly = useMapFiltersStore((state) => state.showFriendsOnly);
    const setShowFriendsOnly = useMapFiltersStore((state) => state.setShowFriendsOnly);
    const showRoutes = useMapFiltersStore((state) => state.showRoutes);
    const setShowRoutes = useMapFiltersStore((state) => state.setShowRoutes);
    
    const handleCurrentViewChange = view => {
        setCurrentView(view);
    };

    const onFilterChange = (value) => {
        if (Array.isArray(value)) {
            setSelectedTypes(value); // replace all
        } else {
            // get current selectedTypes from store
            const current = selectedTypes;
            if (current.includes(value)) {
            setSelectedTypes(current.filter(v => v !== value));
            } else {
            setSelectedTypes([...current, value]);
            }
        }
    };

    const handleFeatureClick = (item, coords) => {
        if (coords !== null) {
            flyTo(coords, 10);
        }
        setSelectedFeature(item);
        setShowDrawerView(false);
    };

    const onTravellerClick = (traveller) => {
        if (traveller.locationCoords) {
            flyTo(traveller.locationCoords, 10);
        }
        setSelectedTraveller(traveller);
        setShowDrawerView(false);
    };

    const mapViews = {
        "AttractionsTravellersList": (<FeaturesTravellersList 
                                            features={filteredPlaces} 
                                            onFeatureClick={handleFeatureClick} 
                                            travellersLoading={usersLoading}
                                            showFriendsOnly={showFriendsOnly}
                                            onTravellerClick={onTravellerClick}
                                            allCountries={allCountries}
                                            locationPermissionDenied={locationPermissionDenied} />),
        "Filters": (<Filters 
                        selectedTypes={selectedTypes} 
                        onFilterChange={onFilterChange}
                        allTypes={allTypes} 
                        toggleSelectedTypes={toggleSelectedTypes}
                        handleShowLocations={handleShowLocations} 
                        showFavouritesOnly={showFavouritesOnly}
                        onToggleFavourites={onToggleFavourites}
                        showTravellers={showTravellers}
                        setShowTravellers={setShowTravellers}
                        showRoutes={showRoutes}
                        setShowRoutes={setShowRoutes}
                        showFriendsOnly={showFriendsOnly}
                        setShowFriendsOnly={setShowFriendsOnly}
                        handleCurrentViewChange={handleCurrentViewChange}
                        toggleIsExpanded={() => setShowDrawerView(!showDrawerView)}
                        selectedCountries={selectedCountries}
                        setSelectedCountries={setSelectedCountries}
                        allCountries={allCountries} />),
        "Search": (<Search 
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        places={filteredPlaces}
                        onFeatureClick={handleFeatureClick} 
                        onTravellerClick={onTravellerClick}
                        handleExitSearch={() => {
                            setSearchValue('');
                            setShowDrawerView(false);
                        }} />),
    }

    const getPreviewText = (featuresCount, travellersCount) => {
        const featuresText = `${featuresCount} Feature${featuresCount !== 1 ? 's' : ''}`;

        if (!user) {
            return (
                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                    {featuresText}
                </Box>
            );
        }

        return (
            <Box sx={{display: 'flex', justifyContent: 'center'}}>
                {featuresText}
                &nbsp;&amp;&nbsp;&nbsp;
                {usersLoading ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <CircularProgress size={12} thickness={4} />
                        <span>Travellers</span>
                    </span>
                ) : (
                    `${travellersCount} Traveller${travellersCount !== 1 ? 's' : ''}`
                )}
            </Box>
        );
    };

    return (
        <div
            style={{
                position: 'relative',
                height: '100%',
                width: '100vw',
                overflow: 'hidden'
            }}
        >
            {/* MAP BACKGROUND */}
            <div style={{ position: 'absolute', inset: 0, zIndex: 0 }}>
                {children}
            </div>

            <div style={{position: 'absolute', top: 0, zIndex: 10, width: '100%', pointerEvents: 'none'}}>
                <TopBar
                    searchValue={searchValue}
                    setSearchValue={setSearchValue}
                    activeFilterCount={selectedTypes.length}
                    isExpanded={showDrawerView && showDrawer}
                    setIsExpanded={setShowDrawerView}
                    handleCurrentViewChange={handleCurrentViewChange}>

                        { mapViews[currentView] }

                </TopBar>
            </div>

            <UserVisibilityButton 
                isDrawerOpen={showDrawerView && showDrawer}
                locationPermissionDenied={locationPermissionDenied}
                updateLocationAndFetchUsers={updateLocationAndFetchUsers} />

            {isMobile && (
                <MobileSwipeableDrawer
                    open={showDrawer}
                    footerHeight={footerHeight}
                    addMarginTop={true}
                    onStateChange={setMobileDrawerState}
                    previewText={getPreviewText(filteredPlaces.length, travellers.length)}>

                    {() => (
                        <FeaturesTravellersList
                            features={filteredPlaces}
                            onFeatureClick={handleFeatureClick}
                            travellersLoading={usersLoading}
                            showFriendsOnly={showFriendsOnly}
                            onTravellerClick={onTravellerClick}
                            allCountries={allCountries}
                            locationPermissionDenied={locationPermissionDenied}
                            />
                    )}
                </MobileSwipeableDrawer>
                )}

            {isMobile && (<>
                <MapDialogMobile 
                    isExpanded={isDesktopViewExpanded}
                    toggleIsExpanded={() => setShowDrawerView(!showDrawerView)}>

                        { mapViews[currentView] }

                </MapDialogMobile>
            </>)}

        </div>
        );
}
